/**
 * TanStack Query hooks for `advertiser-widgets`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	createWidget,
	deleteWidget,
	getLayout,
	reorderWidgets,
	updateWidget,
	advertiserWidgetsQueryKeys,
	type CreateWidgetBody,
	type LayoutResponse,
	type ReorderBody,
	type UpdateWidgetBody,
	type WidgetView,
} from "./api";

export function useWidgetLayout(
	advertiserId: string,
): UseQueryResult<LayoutResponse, Error> {
	return useQuery({
		queryKey: advertiserWidgetsQueryKeys.layout(advertiserId),
		queryFn: () => getLayout(advertiserId),
		enabled: advertiserId.trim().length > 0,
	});
}

export function useCreateWidget(): UseMutationResult<
	WidgetView,
	Error,
	CreateWidgetBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createWidget,
		onSuccess: (row) => {
			qc.invalidateQueries({
				queryKey: advertiserWidgetsQueryKeys.layout(row.advertiser_id),
			});
		},
	});
}

export function useUpdateWidget(): UseMutationResult<
	WidgetView,
	Error,
	{ id: string; body: UpdateWidgetBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => updateWidget(id, body),
		onSuccess: (row) => {
			qc.invalidateQueries({
				queryKey: advertiserWidgetsQueryKeys.layout(row.advertiser_id),
			});
		},
	});
}

export function useDeleteWidget(
	advertiserId: string,
): UseMutationResult<{ id: string; deleted: true }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteWidget,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserWidgetsQueryKeys.layout(advertiserId),
			});
		},
	});
}

export function useReorderWidgets(): UseMutationResult<
	LayoutResponse,
	Error,
	ReorderBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: reorderWidgets,
		onSuccess: (res) => {
			qc.invalidateQueries({
				queryKey: advertiserWidgetsQueryKeys.layout(res.advertiser_id),
			});
		},
	});
}
